import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlideRenderer, PRESET_LIBRARY, getPresetsByCategory, getCategories } from '../../components/carousel';
import { Button, LoadingButton, Dropdown, ContentSpinner, useToast } from '../../components/ui';

const imageStyles = [
  {
    id: 'crayon',
    name: 'Crayon',
    preview: '/images/Crayon.png',
    gradient: ['#FFF5E1', '#FFE4B5', '#FFD700']
  },
  {
    id: 'flat',
    name: 'Flat',
    preview: '/images/Flat.png',
    gradient: ['#4A90E2', '#7B68EE', '#9370DB']
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    preview: '/images/Minimalist.png',
    gradient: ['#FFFFFF', '#F0F0F0', '#E0E0E0']
  },
  {
    id: 'swiss',
    name: 'Swiss Design',
    preview: '/images/Swiss.png',
    gradient: ['#FF0000', '#FFFFFF', '#000000']
  },
  {
    id: 'highend',
    name: 'High-End',
    preview: '/images/High-End.png',
    gradient: ['#2C3E50', '#BDC3C7', '#D4AF37']
  },
  {
    id: 'corporate',
    name: 'Corporate',
    preview: '/images/Corporate.png',
    gradient: ['#1A365D', '#2B6CB0', '#63B3ED']
  },
  {
    id: 'geometric',
    name: 'Geometric',
    preview: '/images/Geometric.png',
    gradient: ['#E74C3C', '#F39C12', '#3498DB']
  }
];

const visualThemes = [
  { id: 'rose', name: 'Rose', colors: ['#FDEFEF', '#F4DFD0', '#DAD0C2', '#CDBBA7'] },
  { id: 'lavender', name: 'Lavender', colors: ['#93B5C6', '#C9CCD5', '#E4D8DC', '#FFE3E3'] },
  { id: 'sunset', name: 'Sunset', colors: ['#FAF3E1', '#F5E7C6', '#FF6D1F', '#222222'] },
  { id: 'ocean', name: 'Ocean', colors: ['#EFECE3', '#8FABD4', '#4A70A9', '#000000'] },
  { id: 'tropical', name: 'Tropical', colors: ['#8CE4FF', '#FEEE91', '#FFA239', '#FF5656'] },
  { id: 'oasisSand', name: 'Oasis Sand', colors: ['#FAF8F1', '#FAEAB1', '#34656D', '#334443'] },
  { id: 'sky', name: 'sky', colors: ['#E2852E', '#F5C857', '#FFEE91', '#ABE0F0'] },
  {id: 'mistySky',name: 'Misty Sky',colors: ['#DEFCF9', '#CADEFC', '#C3BEF0', '#CCA8E9']},
  { id: 'sageMeadow', name: 'Sage Meadow', colors: ['#F1F3E0', '#D2DCB6', '#A1BC98', '#778873'] },
  { id: 'emberRush', name: 'Ember Rush', colors: ['#2D4059', '#EA5455', '#F07B3F', '#FFD460'] },
  { id: 'harvestGlow', name: 'Harvest Glow', colors: ['#84994F', '#FFE797', '#FCB53B', '#B45253'] },
  { id: 'coastalDawn', name: 'Coastal Dawn', colors: ['#FBF5DD', '#A6CDC6', '#16404D', '#DDA853'] }
];

function ContentGeneratorStep2() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState(null);
  const [savedData, setSavedData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContents, setGeneratedContents] = useState([]);
  const [generatedCarousels, setGeneratedCarousels] = useState([]); // Store original JSON data
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [selectedImageStyle, setSelectedImageStyle] = useState('flat');
  const [selectedTheme, setSelectedTheme] = useState('rose');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['threads']); // Default: Threads
  const [selectedPreset, setSelectedPreset] = useState(null); // InstaGen 風格模板
  const [contentType, setContentType] = useState('carousel'); // 'carousel' (圖+文) | 'text' (純文) - 從 step1 讀取
  const [activeCategory, setActiveCategory] = useState('All'); // 模板分類篩選

  // 新增：語氣、長短、語言設定
  const [selectedTone, setSelectedTone] = useState('default'); // 語氣
  const [selectedLanguage, setSelectedLanguage] = useState('zh-TW'); // 語言
  const [selectedPageCount, setSelectedPageCount] = useState('5'); // 頁數
  const [articleLength, setArticleLength] = useState('short'); // 純文字長短

  useEffect(() => {
    loadData();
  }, [navigate]);

  // 載入資料 - 優先從 URL 參數讀取 projectId
  const loadData = async () => {
    // 優先從 URL 參數讀取 projectId
    const urlProjectId = searchParams.get('projectId');
    const currentProjectId = urlProjectId || sessionStorage.getItem('currentProjectId');

    if (!currentProjectId) {
      // 沒有 projectId，返回 Step 1
      toast.warning('找不到專案，請重新開始');
      navigate('/templates/content-generator');
      return;
    }

    // 儲存 projectId 到 state（供後續使用）
    setProjectId(currentProjectId);

    // 嘗試從 Supabase 載入 project 資料
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/projects/${currentProjectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();

        if (result.success && result.project) {
          const project = result.project;
          console.log('📦 從 Supabase 載入 Project:', project.id);

          // 檢查是否有快取的 5 個內容變體
          const hasCachedCarousels = project.cachedCarousels && Array.isArray(project.cachedCarousels) && project.cachedCarousels.length > 0;
          // 檢查 selectedContent 是否為已生成的輪播內容（Array 格式）
          const isGeneratedCarousel = Array.isArray(project.selectedContent);
          const referencePost = project.selectedContent?.referencePost || null;

          // 構建 parsedData 格式
          const parsedData = {
            keywords: project.keywords || [],
            cards: project.pageCount?.toString() || '3',
            contentType: project.contentType || 'carousel',
            language: project.language || 'zh-TW',
            preset: project.preset || 'default',
            topic: project.topic,
            category: project.source,
            query: project.topic || project.keywords?.[0] || '',
            // 如果是已生成的輪播，使用它；否則保留 referencePost
            selectedContent: isGeneratedCarousel ? project.selectedContent : null,
            imageStyle: project.imageStyle,
            theme: project.theme,
            // 參考貼文（用於生成內容）
            referencePost: referencePost
          };

          setSavedData(parsedData);

          // 從 step1 讀取內容類型
          if (parsedData.contentType) {
            setContentType(parsedData.contentType);
          }

          // 恢復 imageStyle 和 theme
          if (project.imageStyle) {
            setSelectedImageStyle(project.imageStyle);
          }
          if (project.theme) {
            setSelectedTheme(project.theme);
          }

          // 恢復語氣、語言、頁數等設定
          if (project.preset) {
            setSelectedTone(project.preset);
          }
          if (project.language) {
            setSelectedLanguage(project.language);
          }
          if (project.pageCount) {
            setSelectedPageCount(project.pageCount.toString());
          }

          // 優先從快取恢復 5 個內容變體
          if (hasCachedCarousels) {
            console.log('📦 從 Supabase 恢復快取的 5 個內容變體');
            // 轉換為顯示格式
            const formattedContents = project.cachedCarousels.map((carousel, idx) => {
              return carousel.map((page, pageIdx) => {
                const parts = [];
                if (page.title) parts.push(`【${page.title}】`);
                if (page.subtitle) parts.push(page.subtitle);
                if (page.description) parts.push(page.description);
                return `Page ${pageIdx + 1}\n${parts.join('\n\n')}`;
              }).join('\n\n---\n\n');
            });
            setGeneratedContents(formattedContents);
            setGeneratedCarousels(project.cachedCarousels);
            toast.success(`已載入 ${project.cachedCarousels.length} 個快取的內容變體`);
            return;
          }

          // 如果有已選擇的單一輪播內容（Array 格式），恢復設定
          if (isGeneratedCarousel && project.selectedContent.length > 0) {
            console.log('📦 恢復已選擇的輪播內容');
            setGeneratedCarousels([project.selectedContent]);
            setSelectedCardIndex(0);
            return;
          }

          // 如果沒有快取內容，開始生成新內容
          console.log('🔄 沒有快取內容，開始生成新內容...');
          console.log('📝 referencePost:', referencePost);
          generateMultipleContents(parsedData);
          return;
        }
      } catch (error) {
        console.error('Failed to load project from Supabase:', error);
      }
    }

    // Fallback: Get data from sessionStorage
    const data = sessionStorage.getItem('contentGeneratorData');
    if (!data) {
      // If no data, redirect back to step 1
      navigate('/templates/content-generator');
      return;
    }
    const parsedData = JSON.parse(data);
    setSavedData(parsedData);

    // 🔍 DEBUG: 確認 referencePost 有沒有傳進來
    console.log('========================================');
    console.log('📥 Step2 收到的資料:');
    console.log('referencePost:', parsedData.referencePost);
    console.log('referenceNews:', parsedData.referenceNews);
    console.log('youtubeUrl:', parsedData.youtubeUrl);
    console.log('========================================');

    // 從 step1 讀取內容類型
    if (parsedData.contentType) {
      setContentType(parsedData.contentType);
    }

    // 檢查是否有快取的生成內容
    if (parsedData.cachedContents && parsedData.cachedCarousels && parsedData.cachedContents.length > 0) {
      console.log('✅ 載入快取的生成內容:', parsedData.cachedContents.length, '個版本');

      // 恢復快取的內容
      setGeneratedContents(parsedData.cachedContents);
      setGeneratedCarousels(parsedData.cachedCarousels);

      // 恢復之前的選擇
      if (parsedData.selectedCardIndex !== undefined) {
        setSelectedCardIndex(parsedData.selectedCardIndex);
      }
      if (parsedData.selectedImageStyle) {
        setSelectedImageStyle(parsedData.selectedImageStyle);
      }
      if (parsedData.selectedTheme) {
        setSelectedTheme(parsedData.selectedTheme);
      }
      if (parsedData.selectedPlatforms) {
        setSelectedPlatforms(parsedData.selectedPlatforms);
      }

      toast.success(`已載入先前生成的 ${parsedData.cachedContents.length} 個內容版本`);
    } else {
      // 沒有快取，自動生成新內容
      console.log('🔄 沒有快取，開始生成新內容...');
      generateMultipleContents(parsedData);
    }
  };

  const processCommasToNewlines = (text, maxCharsPerLine = 20) => {
    if (!text) return text;

    text = text.replace(/。」/g, '」\n');
    const paragraphs = text.split('\n');
    const result = [];

    for (let paragraph of paragraphs) {
      if (paragraph.trim() === '') {
        result.push('');
        continue;
      }

      let currentLine = '';
      let i = 0;

      while (i < paragraph.length) {
        const char = paragraph[i];
        currentLine += char;

        const isPunctuation = char === ',' || char === ',' || char === '。' || char === ';' || char === '、';

        if (currentLine.length >= maxCharsPerLine && isPunctuation) {
          if (char === ',' || char === ',') {
            currentLine = currentLine.slice(0, -1);
          }
          result.push(currentLine.trim());
          currentLine = '';
        }

        i++;
      }

      if (currentLine.trim() !== '') {
        result.push(currentLine.trim());
      }
    }

    return result.join('\n');
  };

  const generateSingleContent = async (contentData, variationIndex) => {
    // Set pages based on user selection from step1
    // For carousel: use 'cards' value (3, 5, 10)
    // For text-only: use articleLength (short=5, long=10)
    let pages;
    if (contentData.contentType === 'carousel' && contentData.cards) {
      pages = parseInt(contentData.cards, 10) || 10;
    } else {
      const isLongForm = contentData.articleLength === 'long';
      pages = isLongForm ? 10 : 5;
    }

    // Adjust tone description based on style
    const styleMap = {
      'professional': 'Professional, clear, concise',
      'casual': 'Relaxed, fun, conversational',
      'friendly': 'Friendly, warm, approachable'
    };
    const styleDescription = styleMap[contentData.preset] || 'Professional, clear, concise';

    // 語言設定
    const langCode = contentData.language || 'zh-TW';
    const isChineseOutput = langCode === 'zh-TW' || langCode === 'zh-CN';
    const languageInstruction = isChineseOutput
      ? '⚠️ 必須使用繁體中文輸出所有內容！所有 title、subtitle、description 都必須是繁體中文！'
      : `Output all content in ${langCode === 'en' ? 'English' : langCode}`;

    // System Prompt (fixed)
    const systemPrompt = `You are an Instagram carousel content generator with web search capabilities.

CRITICAL RULES:
1. ❌ NEVER mention past years (2024, 2023, 2022, etc.)
2. ✅ Only use 2025 or current/future references
3. ✅ You can search the web for latest information
4. ✅ Use real-time data and current trends
5. ✅ Focus on "latest", "current", "2025", "upcoming"
6. ⚠️ LANGUAGE: ${languageInstruction}

Always output ONLY valid JSON.
Return an array of objects:
[
  {
    "page": 1,
    "title": "",
    "subtitle": "",
    "description": ""
  }
]
Never add hyphens. Never add extra text outside JSON.`;

    // Build reference content section if available
    let referenceSection = '';
    if (contentData.referencePost) {
      // Threads post reference
      referenceSection = `
REFERENCE POST TO REWRITE:
"""
${contentData.referencePost.text || contentData.referencePost.content || ''}
"""
⚠️ Use this post as inspiration and rewrite it in your own style. DO NOT copy directly.
`;
    } else if (contentData.referenceNews) {
      // Google Trends news reference
      referenceSection = `
REFERENCE NEWS ARTICLE:
Title: ${contentData.referenceNews.title || ''}
Summary: ${contentData.referenceNews.snippet || ''}
Source: ${contentData.referenceNews.source || ''}
⚠️ Use this news as the main topic and create original content based on it.
`;
    } else if (contentData.youtubeUrl) {
      // YouTube reference
      referenceSection = `
REFERENCE YOUTUBE VIDEO:
URL: ${contentData.youtubeUrl}
⚠️ Create content based on this video's topic.
`;
    } else if (contentData.selectedContent?.idea) {
      // Idea to Post - user's original idea
      referenceSection = `
USER'S ORIGINAL IDEA:
"""
${contentData.selectedContent.idea}
"""
⚠️ Expand this idea into a compelling carousel with storytelling and valuable insights.
`;
    }

    // 🔍 DEBUG: 確認 referenceSection 有沒有被建立
    console.log('========================================');
    console.log('🔧 generateSingleContent - referenceSection:');
    console.log('contentData.referencePost:', contentData.referencePost);
    console.log('contentData.referenceNews:', contentData.referenceNews);
    console.log('referenceSection:', referenceSection);
    console.log('========================================');

    // User Prompt (dynamic content replacement)
    const userPrompt = `Generate an Instagram carousel in JSON format about the LATEST information on this topic.

Topic: ${contentData.query}
Pages: ${pages}
Style: ${styleDescription}
Language: ${isChineseOutput ? '繁體中文 (Traditional Chinese)' : langCode}
${referenceSection}
IMPORTANT CONSTRAINTS:
- ❌ DO NOT mention any year before 2025
- ✅ Search the web if needed for latest information
- ✅ Focus on current trends and 2025 updates
- ✅ Use phrases like "currently", "latest", "in 2025", "upcoming"
- ✅ Provide real, verified information from web search
${referenceSection ? '- ✅ Rewrite and expand on the reference content with your own insights' : ''}
${isChineseOutput ? '- ⚠️ 所有內容必須用繁體中文撰寫！' : ''}

Output JSON only.`;

    // 🔍 DEBUG: 確認最終送出的 userPrompt
    console.log('========================================');
    console.log('📤 送出的 userPrompt:');
    console.log(userPrompt);
    console.log('========================================');

    const token = localStorage.getItem('token');
    const response = await fetch('/api/ai/generate-carousel', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        systemPrompt: systemPrompt,
        userPrompt: userPrompt,
        topic: contentData.query,
        pages: pages,
        language: contentData.language || 'zh-TW',
        style: styleDescription
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error('Login expired, please log in again');
      }
      throw new Error(`Generation failed: ${errorData.error || response.statusText}`);
    }

    const responseData = await response.json();

    if (!responseData.success || !responseData.data) {
      throw new Error('Generated content format is incorrect');
    }

    // Convert JSON format to display text
    const carouselData = responseData.data;
    const formattedContent = carouselData.map((page, index) => {
      const parts = [];
      if (page.title) parts.push(`【${page.title}】`);
      if (page.subtitle) parts.push(page.subtitle);
      if (page.description) parts.push(page.description);
      return `Page ${index + 1}\n${parts.join('\n\n')}`;
    }).join('\n\n---\n\n');

    // Return formatted text and original JSON
    return {
      formatted: formattedContent,
      carousel: carouselData
    };
  };

  const generateMultipleContents = async (data) => {
    const contentData = data || savedData;
    if (!contentData) return;

    setIsGenerating(true);
    setGeneratedContents([]);
    setGeneratedCarousels([]);

    try {
      // Generate 5 variations in parallel
      const promises = Array.from({ length: 5 }, (_, i) =>
        generateSingleContent(contentData, i)
      );

      const results = await Promise.all(promises);

      // Separate formatted text and original JSON
      const formattedContents = results.map(r => r.formatted);
      const carouselData = results.map(r => r.carousel);

      setGeneratedContents(formattedContents);
      setGeneratedCarousels(carouselData);

      // 自動儲存生成的內容到 Supabase（使用 state 中的 projectId）
      const currentProjectId = projectId || searchParams.get('projectId');
      if (currentProjectId) {
        try {
          const token = localStorage.getItem('token');
          await fetch(`/api/projects/${currentProjectId}/step`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              step: 2, // 目前在 Step 2
              generationStatus: 'content_generated',
              // 儲存所有 5 個生成的變體（供後續恢復使用）
              cachedCarousels: carouselData
            })
          });
          console.log('✅ 已儲存 5 個生成的內容變體到 Supabase');
        } catch (error) {
          console.error('Failed to save generated contents:', error);
        }
      }

      toast.success('Successfully generated 5 content variations!');
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.message || 'Generation failed, please try again');
    } finally {
      setIsGenerating(false);
    }
  };

  // 儲存當前進度並返回首頁
  const handleGoHome = async () => {
    // 儲存當前進度到 Supabase（使用 state 中的 projectId）
    const currentProjectId = projectId || searchParams.get('projectId');
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');

        // 如果有選擇的內容，儲存到 Supabase
        const updateData = {
          step: 2,
          imageStyle: selectedImageStyle,
          theme: selectedTheme,
          contentType: contentType,
          preset: selectedPreset
        };

        // 如果有選擇內容，也儲存
        if (selectedCardIndex !== null && generatedCarousels[selectedCardIndex]) {
          updateData.selectedContent = generatedCarousels[selectedCardIndex];
        }

        await fetch(`/api/projects/${currentProjectId}/step`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        console.log('✅ Project step 2 saved to Supabase');
        toast.success('進度已儲存，可從「Your Projects」繼續編輯');
      } catch (error) {
        console.error('Failed to save project:', error);
        toast.warning('儲存失敗，請稍後再試');
      }
    }

    // 導航到首頁
    navigate('/home');
  };

  const handleNext = async () => {
    if (selectedCardIndex === null) {
      toast.warning('Please select a content version');
      return;
    }

    // 使用 state 中的 projectId（優先）或從 URL 參數讀取
    const currentProjectId = projectId || searchParams.get('projectId');

    if (!currentProjectId) {
      toast.error('找不到專案 ID，請重新開始');
      navigate('/templates/content-generator');
      return;
    }

    // Update project in Supabase - 更新到 step 3（代表即將進入 Step 3）
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/projects/${currentProjectId}/step`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          step: 3, // 進入 Step 3
          selectedContent: generatedCarousels[selectedCardIndex],
          imageStyle: selectedImageStyle,
          theme: selectedTheme,
          pageCount: parseInt(selectedPageCount || '5'),
          contentType: contentType,
          preset: selectedTone,
          language: selectedLanguage
        })
      });
      console.log('✅ Project step updated to 3 (entering Step 3)');
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('儲存失敗，請稍後再試');
      return;
    }

    // 使用 URL 參數導航到 Step 3
    navigate(`/templates/content-generator/step3?projectId=${currentProjectId}`);
  };

  return (
    <div className="relative min-h-screen bg-white flex items-start justify-center py-8 px-16">
      {/* Home Button - Top Left */}
      <Button
        onClick={handleGoHome}
        variant="primary"
        size="md"
        icon="mdi:home"
        className="fixed top-8 left-8 z-[100]"
      >
        Home
      </Button>

      {/* Back Button - Bottom Left */}
      <Button
        onClick={() => navigate(-1)}
        variant="ghost"
        size="sm"
        icon="mdi:arrow-left"
        className="absolute bottom-4 left-4 bg-gray-500/50 text-white hover:bg-gray-600/70"
      >
        Back
      </Button>

      {/* Main Content Box */}
      <div className="max-w-[1400px] w-full bg-white rounded-none py-12 px-20 shadow-none">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-black mb-2">Content Preview</h1>
          {savedData && (
            <p className="text-base text-gray-600">Topic: {savedData.query}</p>
          )}
        </div>

        {/* Content Cards Carousel */}
        <div className="mb-16">
          {isGenerating ? (
            <ContentSpinner text="Generating 5 content variations..." />
          ) : (
            <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-slate-300">
              {generatedContents.map((content, index) => {
                const isSelected = selectedCardIndex === index;
                return (
                  <div
                    key={index}
                    onClick={() => setSelectedCardIndex(index)}
                    className={`min-w-[360px] max-w-[360px] h-[480px] bg-white border rounded-xl p-8 flex flex-col gap-4 snap-start transition-all duration-300 cursor-pointer relative ${
                      isSelected
                        ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.1)] -translate-y-1'
                        : 'border-gray-200 hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-slate-500">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-sm leading-relaxed text-slate-800 overflow-y-auto whitespace-pre-wrap scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200">
                      {content}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 內容設定區塊 - Content Type, Tone, Language, Page Count */}
        {generatedContents.length > 0 && (
          <div className="pt-8 border-t border-gray-200">
            <h2 className="text-xl font-bold text-black mb-6">內容設定</h2>

            {/* Content Type Selection */}
            <div className="mb-6">
              <div className="text-sm font-semibold text-slate-500 mb-3">
                內容類型
              </div>
              <div className="flex gap-4">
                {[
                  { id: 'carousel', name: '圖 + 文', icon: 'mdi:image-multiple', color: '#8B5CF6' },
                  { id: 'text', name: '純文字', icon: 'mdi:text', color: '#3B82F6' }
                ].map((type) => {
                  const isSelected = contentType === type.id;
                  return (
                    <div
                      key={type.id}
                      onClick={() => setContentType(type.id)}
                      className={`flex-1 p-4 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-3 ${
                        isSelected
                          ? 'border-2'
                          : 'border border-gray-200 bg-white'
                      }`}
                      style={{
                        borderColor: isSelected ? type.color : undefined,
                        background: isSelected ? `${type.color}08` : undefined
                      }}
                    >
                      <Icon
                        icon={type.icon}
                        className="text-xl"
                        style={{ color: isSelected ? type.color : '#64748b' }}
                      />
                      <span
                        className="text-sm font-semibold"
                        style={{ color: isSelected ? type.color : '#1e293b' }}
                      >
                        {type.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dropdowns Row */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {/* Page Count / Article Length */}
              {contentType === 'carousel' ? (
                <div>
                  <Dropdown
                    label="輪播頁數"
                    value={selectedPageCount}
                    onChange={(value) => setSelectedPageCount(value)}
                    size="md"
                    fullWidth
                    options={[
                      { value: '3', label: '3 頁' },
                      { value: '5', label: '5 頁' },
                      { value: '10', label: '10 頁' }
                    ]}
                  />
                </div>
              ) : (
                <div>
                  <Dropdown
                    label="文章長度"
                    value={articleLength}
                    onChange={(value) => setArticleLength(value)}
                    size="md"
                    fullWidth
                    options={[
                      { value: 'short', label: '短篇' },
                      { value: 'long', label: '長篇' }
                    ]}
                  />
                </div>
              )}

              {/* Tone */}
              <div>
                <Dropdown
                  label="語氣風格"
                  value={selectedTone}
                  onChange={(value) => setSelectedTone(value)}
                  size="md"
                  fullWidth
                  options={[
                    { value: 'default', label: '預設' },
                    { value: 'professional', label: '專業' },
                    { value: 'casual', label: '輕鬆' },
                    { value: 'friendly', label: '親切' }
                  ]}
                />
              </div>

              {/* Language */}
              <div>
                <Dropdown
                  label="輸出語言"
                  value={selectedLanguage}
                  onChange={(value) => setSelectedLanguage(value)}
                  size="md"
                  fullWidth
                  options={[
                    { value: 'zh-TW', label: '繁體中文' },
                    { value: 'en-US', label: 'English' },
                    { value: 'ja-JP', label: '日本語' }
                  ]}
                />
              </div>
            </div>

            {/* Regenerate Button */}
            <LoadingButton
              onClick={() => generateMultipleContents({
                ...savedData,
                cards: selectedPageCount,
                preset: selectedTone,
                language: selectedLanguage,
                articleLength: articleLength,
                contentType: contentType
              })}
              loading={isGenerating}
              loadingText="生成中..."
              icon="mdi:refresh"
              variant="primary"
              size="lg"
              className="w-full"
            >
              重新生成內容
            </LoadingButton>
          </div>
        )}

        {/* AI Image Style Section - 只在選擇「圖＋文」時顯示 */}
        {generatedContents.length > 0 && contentType === 'carousel' && (
          <div className="pt-8">
            <h2 className="text-xl font-bold text-black mb-6">Image Style</h2>
            <div className="mb-4 text-sm text-slate-500">
              AI Images
            </div>
            <div className="grid grid-cols-6 gap-4 mb-12">
              {imageStyles.map((style) => {
                const isSelected = selectedImageStyle === style.id;
                return (
                  <div
                    key={style.id}
                    onClick={() => setSelectedImageStyle(style.id)}
                    className={`h-[140px] rounded-lg cursor-pointer transition-all duration-200 relative overflow-hidden ${
                      isSelected
                        ? 'border-2 border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
                        : 'border border-gray-200 hover:-translate-y-1 hover:shadow-lg'
                    }`}
                    style={{
                      background: style.preview
                        ? `url(${style.preview}) center/cover`
                        : `linear-gradient(135deg, ${style.gradient[0]} 0%, ${style.gradient[1]} 50%, ${style.gradient[2]} 100%)`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 flex flex-col items-center justify-center gap-2">
                      <Icon icon={style.icon} className="text-3xl text-white" />
                      <div className="text-xs font-semibold text-white text-center">
                        {style.name}
                      </div>
                      {isSelected && (
                        <Icon icon="mdi:check-circle" className="absolute top-2 right-2 text-xl text-blue-500" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Visual Themes Section - 只在選擇「圖＋文」時顯示 */}
        {generatedContents.length > 0 && contentType === 'carousel' && (
          <div className="pt-0">
            <h2 className="text-xl font-bold text-black mb-6">Visual Art Style</h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
              {visualThemes.map((theme) => {
                const isSelected = selectedTheme === theme.id;
                return (
                  <div
                    key={theme.id}
                    onClick={() => setSelectedTheme(theme.id)}
                    className={`h-[120px] rounded-lg cursor-pointer transition-all duration-200 relative overflow-hidden flex flex-col p-0 ${
                      isSelected
                        ? 'border-2 border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
                        : 'border border-gray-200'
                    }`}
                  >
                    {theme.colors.map((color) => (
                      <div
                        key={color}
                        className="flex-1 w-full"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <div className="absolute bottom-2 left-2 text-[11px] font-semibold text-white bg-black/60 py-1 px-2 rounded">
                      {theme.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* InstaGen Style Templates Section - 已隱藏 (Coming Soon) */}
        {/* {generatedContents.length > 0 && contentType === 'carousel' && (
          <div style={{ ...styles.themesSection, marginTop: '2rem' }}>
            ... Style Template section hidden ...
          </div>
        )} */}
      </div>

      {/* Next Button */}
      {generatedContents.length > 0 && (
        <Button
          onClick={handleNext}
          disabled={selectedCardIndex === null}
          variant="primary"
          size="sm"
          iconRight="mdi:arrow-right"
          className="absolute bottom-4 right-4 z-[100]"
        >
          Next Step
        </Button>
      )}
    </div>
  );
}

export default ContentGeneratorStep2;
